/**
 * ECS Client Type Definitions
 * -----------------------------------------------------------------------------
 * Centralized TypeScript interfaces and types for ECS client functionality.
 */

import type { AccountInfo } from "@azure/msal-browser";
import type { FeatureFlagTypes } from "./featureFlagRegistry";

/**
 * Application Configuration Type
 * This represents the complete configuration object that the app uses.
 * It's derived from the feature flag registry for type safety.
 */
export interface AppConfiguration {
  featureFlags: FeatureFlagTypes;
}

/**
 * EcsInitOptions
 * -----------------------------------------------------------------------------
 * Encapsulates all contextual parameters required for ECS client initialization.
 * This object is passed from the application entry point and contains user context,
 * environment, deployment ring, region, release train, locale, and other options
 * relevant for ECS configuration and targeting.
 */
export interface EcsInitOptions {
  /** The current user's account info (required for ECS filtering) */
  userAccount: AccountInfo;
  /** Optional Teams ring identifier (e.g., "dogfood", "prod") */
  ring?: string;
  /** Optional environment name (e.g., "dev", "staging", "prod") */
  environment?: string;
  /** Optional region identifier (e.g., "NA", "EU", "APAC") */
  region?: string;
  /** Optional release train number for targeted configs */
  releaseTrainNumber?: string;
  /** Optional locale string (e.g., "en-US", "fr-FR") */
  locale?: string;
  /** Optional client type identifier */
  clientType?: string;
  /** Optional additional config option for ECS */
  configOption?: string;
}

/**
 * ECS Configuration State
 * Used in Redux store to track ECS configuration status.
 */
export interface EcsConfigState {
  /** Current application configuration */
  config: AppConfiguration | null;
  /** Country code from ECS headers */
  countryCode: string | undefined;
  /** Whether ECS is currently loading configuration */
  isLoading: boolean;
  /** Last error message, if any */
  error: string | null;
  /** Timestamp of last successful configuration load */
  lastUpdated: number | null;
}

/**
 * Configuration Change Listener
 * Function signature for listening to configuration changes.
 */
export type ConfigChangeListener = (config: AppConfiguration) => void;

/**
 * ECS Client Status
 * Represents the current status of the ECS client.
 */
export enum EcsClientStatus {
  NotInitialized = "not_initialized",
  Initializing = "initializing",
  Ready = "ready",
  Error = "error",
  Disposed = "disposed",
}

/**
 * ECS Fetch Result
 * Result type for ECS fetch operations.
 */
export interface EcsFetchResult {
  success: boolean;
  config?: AppConfiguration;
  error?: string;
  fromCache?: boolean;
  timestamp: number;
}

/**
 * ECS Client Options
 * Configuration options for ECS client behavior.
 */
export interface EcsClientOptions {
  /** Enable automatic fetch scheduling */
  enableAutoFetch?: boolean;
  /** Cache duration in milliseconds */
  cacheDuration?: number;
  /** Maximum retry attempts */
  maxRetries?: number;
  /** Base delay for exponential backoff */
  baseDelayMs?: number;
}

/**
 * User Filter Information
 * Extracted from user account for ECS targeting.
 */
export interface UserFilterInfo {
  tenantId?: string;
  userId?: string;
  userPrincipalName?: string;
  environment?: string;
}